import { ResultOf } from '@graphql-typed-document-node/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import MutationKeys from 'queries/MutationKeys';
import { ticketsCartQuery } from 'queries/tickets/queries';
import { AppSyncTicketsGraphQLError } from 'queries/tickets/types';
import { useTicketsClient } from 'services/api';
import { CartItemInput, CartUpdateDocument } from 'types/api/tickets.types';

import { useCartVersion } from './useCartVersion';
import { useTicketingCartId, useTicketingEventId } from './utils';

type ReplaceCartVariables = {
  items: Array<CartItemInput>;
};

export const useTicketsReplaceCart = () => {
  const { cartId, setCartId } = useTicketingCartId();
  const { eventId } = useTicketingEventId();
  const version = useCartVersion();

  const queryClient = useQueryClient();
  const ticketsClient = useTicketsClient();

  const mutation = useMutation<
    ResultOf<typeof CartUpdateDocument>,
    AppSyncTicketsGraphQLError,
    ReplaceCartVariables
  >({
    mutationKey: MutationKeys.tickets.cartUpdate(cartId),
    mutationFn: ({ items }) =>
      ticketsClient.request(CartUpdateDocument, {
        eventId,
        cartId,
        input: {
          action: {
            action: 'ReplaceItems',
            items,
          },
          version,
        },
      }),
    onMutate: () => {
      const queryKey = ticketsCartQuery({ eventId, cartId }).queryKey;
      return queryClient.cancelQueries({ queryKey });
    },
    onSuccess: ({ cart }) => {
      if (cart?.id) {
        queryClient.setQueryData(
          ticketsCartQuery({ eventId, cartId: cart.id }).queryKey,
          { cart },
        );
        setCartId(cart.id);
      }
    },
  });

  return {
    replaceCart: mutation.mutate,
    isLoading: mutation.isPending && !mutation.isPaused,
    error: mutation.error,
  };
};
